// from https://github.com/mui-org/material-ui/blob/master/packages/material-ui-lab/src/Pagination/usePagination.js
import clsx from "clsx";
import { PaginationItem } from "./PaginationItem";

type Props = {
  total: number;
  page: number;
  nbPerPage: number;
  onClick: (event: React.ChangeEvent<unknown>, value: number) => void;
  hidePageLink?: boolean;
  className?: string;
};
export const Pagination = ({ total, page, nbPerPage, onClick, hidePageLink, className }: Props) => {
  const boundaryCount = 1;
  const siblingCount = 1;
  const disabled = false;
  const hideNextButton = false;
  const hidePrevButton = false;
  const showFirstButton = true;
  const showLastButton = true;
  const count = Math.ceil(total / nbPerPage);

  const handleClick = (event: React.ChangeEvent<unknown>, value: number | null) => {
    if (value === null || value <= 0 || value > count) return;

    onClick(event, value);
  };

  const range = (start: number, end: number) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, i) => start + i);
  };

  const startPages = range(1, Math.min(boundaryCount, count));
  const endPages = range(Math.max(count - boundaryCount + 1, boundaryCount + 1), count);

  const siblingsStart = Math.max(
    Math.min(
      // Natural start
      page - siblingCount,
      // Lower boundary when page is high
      count - boundaryCount - siblingCount * 2 - 1,
    ),
    // Greater than startPages
    boundaryCount + 2,
  );

  const siblingsEnd = Math.min(
    Math.max(
      // Natural end
      page + siblingCount,
      // Upper boundary when page is low
      boundaryCount + siblingCount * 2 + 2,
    ),
    // Less than endPages
    (endPages[0] || 0) - 2,
  );

  const itemList: Array<
    "page" | "first" | "last" | "next" | "previous" | "start-ellipsis" | "end-ellipsis" | number | string
  > = [
    ...(showFirstButton ? ["first"] : []),
    ...(hidePrevButton ? [] : ["previous"]),
    ...startPages,

    // Start ellipsis
    // eslint-disable-next-line no-nested-ternary
    ...(siblingsStart > boundaryCount + 2
      ? ["start-ellipsis"]
      : boundaryCount + 1 < count - boundaryCount
        ? [boundaryCount + 1]
        : []),

    // Sibling pages
    ...range(siblingsStart, siblingsEnd),

    // End ellipsis
    // eslint-disable-next-line no-nested-ternary
    ...(siblingsEnd < count - boundaryCount - 1
      ? ["end-ellipsis"]
      : count - boundaryCount > boundaryCount
        ? [count - boundaryCount]
        : []),

    ...endPages,
    ...(hideNextButton ? [] : ["next"]),
    ...(showLastButton ? ["last"] : []),
  ];

  const buttonPage = (
    type: "page" | "first" | "last" | "next" | "previous" | "start-ellipsis" | "end-ellipsis" | string,
  ) => {
    switch (type) {
      case "first":
        return 1;
      case "previous":
        return page - 1;
      case "next":
        return page + 1;
      case "last":
        return count;
      default:
        return null;
    }
  };

  const items = itemList.map((item) => {
    return typeof item === "number"
      ? {
          onClick: (event: React.ChangeEvent<unknown>) => {
            handleClick(event, item);
          },
          type: "page",
          page: item,
          selected: item === page,
          disabled,
          hidePageLink,
        }
      : {
          onClick: (event: React.ChangeEvent<unknown>) => {
            handleClick(event, buttonPage(item));
          },
          type: item,
          page: buttonPage(item),
          selected: false,
          disabled:
            disabled ||
            (item.indexOf("ellipsis") === -1 && (item === "next" || item === "last" ? page >= count : page <= 1)),
          hidePageLink,
        };
  });

  return (
    <ul className={clsx(className, "flex items-center justify-center flex-wrap list-none m-0 p-0 w-full")}>
      {items.map((item, index) => (
        <li key={`page-number-${index}`}>
          <PaginationItem {...item} />
        </li>
      ))}
    </ul>
  );
};
